import { useMutation, useQueryClient } from "@tanstack/react-query";
import type {
  BankAccount,
  UpdateBankAccountPayload,
} from "../repositories/bankRepository";
import { bankRepository } from "../repositories/bankRepository";
import {
  bankAccountOptionsQueryKey,
  bankAccountQueryKey,
} from "./useBankAccountQueries";

type Variables = {
  id: string;
  payload: UpdateBankAccountPayload;
};

type Context = {
  previous?: BankAccount | null;
};

/**
 * Mutation hook to update a bank account.
 *
 * - Performs optimistic update on the single bank-account query.
 * - Rolls back on error.
 * - On success updates cache from response.
 * - On settled invalidates account and options queries.
 */
export function useUpdateBankAccountMutation() {
  const qc = useQueryClient();

  return useMutation<BankAccount, Error, Variables, Context>({
    mutationKey: ["bank-account", "update"],
    mutationFn: ({ id, payload }) =>
      bankRepository.updateBankAccount(id, payload),

    // optimistic update
    onMutate: async ({ id, payload }) => {
      const key = bankAccountQueryKey(id);
      // cancel any outgoing refetches so they don't overwrite our optimistic update
      await qc.cancelQueries({ queryKey: key });

      const previous = qc.getQueryData<BankAccount>(key) ?? null;

      // apply optimistic update by merging payload into cached bank account
      qc.setQueryData<BankAccount | null>(key, (old) =>
        old
          ? ({ ...old, ...payload } as BankAccount)
          : (payload as unknown as BankAccount)
      );

      return { previous };
    },

    onError: (_err, _vars, context) => {
      const key = bankAccountQueryKey(_vars.id);
      // rollback to previous value
      if (context?.previous !== undefined) {
        qc.setQueryData(key, context.previous);
      } else {
        qc.invalidateQueries({ queryKey: key });
      }
    },

    onSuccess: (updated) => {
      // update cache from server response to ensure canonical state
      qc.setQueryData(bankAccountQueryKey(updated.id), updated);
    },

    onSettled: (_data, _error, vars) => {
      const promises: Promise<unknown>[] = [
        qc.invalidateQueries({ queryKey: bankAccountQueryKey(vars.id) }),
        qc.invalidateQueries({ queryKey: bankAccountOptionsQueryKey() }),
      ];
      return Promise.all(promises);
    },

    retry: 0,
  });
}
